// purpose of caching data from backend
import { useQuery } from "@tanstack/react-query";
import { useBusiness } from "@hooks/useBusiness";
import { fetchTransactionsList } from "@/services/transactionsApi";

const DAY = 24 * 60 * 60 * 1000;
const WINDOW_DAYS = 30;

const sum = (list, pick) => list.reduce((acc, item) => acc + (pick(item) || 0), 0);

const pctChange = (current, previous) => {
  if (!previous) return current ? 100 : 0;
  return Number((((current - previous) / previous) * 100).toFixed(1));
};

/**
 * The POS stores transactions, not pre-aggregated metrics, so the home tiles are
 * derived here: a trailing 30-day window compared against the 30 days before it.
 */
function summarize(transactions) {
  const now = Date.now();
  const windowStart = now - WINDOW_DAYS * DAY;
  const priorStart = windowStart - WINDOW_DAYS * DAY;

  const at = (txn) => new Date(txn.date).getTime();
  // Voided sales must not count toward revenue.
  const completed = transactions.filter((t) => t.status !== "voided" && t.date);

  const current = completed.filter((t) => at(t) >= windowStart);
  const prior = completed.filter((t) => at(t) >= priorStart && at(t) < windowStart);

  const revenue = sum(current, (t) => t.total);
  const priorRevenue = sum(prior, (t) => t.total);

  // Daily revenue across the window, oldest first - the shape the charts want.
  const salesPerformance = Array.from({ length: WINDOW_DAYS }, (_, i) => {
    const dayStart = windowStart + i * DAY;
    const dayTotal = sum(
      current.filter((t) => at(t) >= dayStart && at(t) < dayStart + DAY),
      (t) => t.total
    );
    return { date: new Date(dayStart).toISOString().slice(0, 10), value: dayTotal };
  });

  // Units sold per product across the window.
  const productTotals = new Map();
  current.forEach((txn) => {
    (txn.items || []).forEach((item) => {
      const name = item.name || "Unnamed";
      const entry = productTotals.get(name) || { name, quantity: 0, revenue: 0 };
      entry.quantity += Number(item.quantity) || 0;
      entry.revenue += Number(item.lineTotal) || 0;
      productTotals.set(name, entry);
    });
  });

  const named = current.filter((t) => t.customer?.name);

  return {
    totalRevenue: revenue,
    totalRevenueChange: pctChange(revenue, priorRevenue),

    totalOrders: current.length,
    totalOrdersChange: pctChange(current.length, prior.length),

    averageOrderValue: current.length ? Math.round(revenue / current.length) : 0,
    averageOrderValueChange: pctChange(
      current.length ? revenue / current.length : 0,
      prior.length ? priorRevenue / prior.length : 0
    ),

    // The POS has no cart concept, so there is nothing to abandon. Left at zero
    // rather than invented, so the tile reads as empty instead of wrong.
    abandonedRate: 0,
    abandonedRateChange: 0,
    abandonedStats: {},

    salesPerformance,
    topSellingProducts: [...productTotals.values()]
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5),
    customerDistribution: {
      returning: named.length,
      walkIn: current.length - named.length,
    },
  };
}

export const useDashboardAnalytics = () => {
  const { businessId, users } = useBusiness();

  // cache data using react query
  return useQuery({
    queryKey: ["dashboard-analytics", businessId, Object.keys(users).length],
    queryFn: async () => summarize(await fetchTransactionsList(businessId, users)),
    enabled: Boolean(businessId),

    // cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
